import {
  AuthApi,
  Configuration,
  Middleware,
  PortalApi,
  ProfileApi,
} from '@helpscout/customer-portal-api-typescript-fetch-client'

export type ApiErrorKind =
  | 'bad-request'
  | 'unauthorized'
  | 'forbidden'
  | 'not-found'
  | 'rate-limited'
  | 'server'
  | 'network'

export class ApiResponseError extends Error {
  readonly kind: ApiErrorKind

  constructor(kind: ApiErrorKind) {
    super(kind)
    this.name = 'ApiResponseError'
    this.kind = kind
  }
}

type UnauthorizedHandler = () => void

let unauthorizedHandler: UnauthorizedHandler | null = null

export const setUnauthorizedHandler = (handler: UnauthorizedHandler | null) => {
  unauthorizedHandler = handler
}

const STATUS_KINDS: Record<number, ApiErrorKind> = {
  400: 'bad-request',
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not-found',
  429: 'rate-limited',
}

const nameOf = (error: unknown): unknown =>
  typeof error === 'object' && error !== null
    ? (error as { name?: unknown }).name
    : undefined

// The generated client is published as ES5, so its ResponseError/FetchError
// (subclasses of Error) fail `instanceof`. Detect them by name instead. The
// abort error is a DOMException, matched the same way.
const isResponseError = (
  error: unknown
): error is { response: { status: number } } =>
  nameOf(error) === 'ResponseError'

const isFetchError = (error: unknown): error is { cause?: unknown } =>
  nameOf(error) === 'FetchError'

const isAbort = (error: unknown): boolean => nameOf(error) === 'AbortError'

// Translates the generated client's errors into our ApiResponseError contract,
// and routes a 401 into the unauthorized handler (session expiry → logged out).
// An aborted request is rethrown untouched so React Query treats it as a
// cancellation rather than a failure.
const toApiError = (error: unknown): unknown => {
  if (isResponseError(error)) {
    const { status } = error.response
    if (status === 401) unauthorizedHandler?.()
    return new ApiResponseError(STATUS_KINDS[status] ?? 'server')
  }
  if (isFetchError(error)) {
    if (isAbort(error.cause)) return error.cause
    return new ApiResponseError('network')
  }
  return error
}

/** Runs a generated-client call, mapping its errors onto ApiResponseError. */
export const call = async <T>(fn: () => Promise<T>): Promise<T> => {
  try {
    return await fn()
  } catch (error) {
    throw toApiError(error)
  }
}

const readCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

// Echo the PORTAL_XSRF_TOKEN cookie back as the X-PORTAL-XSRF-TOKEN header on mutating requests.
export const csrfMiddleware: Middleware = {
  pre: ({ url, init }) => {
    const method = (init.method ?? 'GET').toUpperCase()
    if (method !== 'GET' && method !== 'HEAD') {
      const token = readCookie('PORTAL_XSRF_TOKEN')
      if (token) {
        init.headers = {
          ...(init.headers as Record<string, string>),
          'X-PORTAL-XSRF-TOKEN': token,
        }
      }
    }
    return Promise.resolve({ url, init })
  },
}

// Same-origin and cookie-authenticated: an empty basePath keeps the relative
// `/v1/*` URLs the portal is served alongside, and credentials carry the
// portal session cookie.
const configuration = new Configuration({
  basePath: '',
  credentials: 'include',
  middleware: [csrfMiddleware],
})

export const authApi = new AuthApi(configuration)
export const portalApi = new PortalApi(configuration)
export const profileApi = new ProfileApi(configuration)
